import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import menuPortalProps from 'helpers/reactSelectPortal';
import { isAfter } from 'date-fns';
import { toast } from 'react-toastify';
import { FaBusinessTime, FaInfoCircle } from 'react-icons/fa';
import { getExchangeRate } from 'redux/settingsSlice';
import { createBookingShuttle, updateBookingShuttle } from 'redux/booking/travel/travelBookingSlice';
import TinymceEditor from 'components/common/TinymceEditor';
import BookingUploadAttachment from '../../BookingUploadAttachment';
import { Drawer, Button, Alert, FormRow, CurrencySelect } from 'components/common';
// Per transport-type icon + accent colour, shared with the Expense summary.
import { SHUTTLE_META } from 'components/app/bookings/travel/booking/expenseIcons';

import { INPUT_CLS as inputClass } from 'helpers/formStyles';
const hint = 'tw-mt-1 tw-text-xs tw-text-slate-500 tw-flex tw-items-center tw-gap-1';

const formatShuttleOption = (opt) => {
  const Icon = opt.icon;
  return (
    <span className="tw-flex tw-items-center tw-gap-2">
      <span className="tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-text-white" style={{ backgroundColor: opt.color }}>
        {Icon && <Icon size={12} />}
      </span>
      <span className="tw-text-slate-700">{opt.label}</span>
    </span>
  );
};

const ShuttleForm = ({ showModal, setshowModal, booking, shuttle, transit }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues, reset, watch } = useForm();
  const { usdExchangeRateData } = useSelector((state) => state.settings);
  const [files, setFiles] = useState([]);

  const shuttleNameOptions = useMemo(() => SHUTTLE_META.map((m) => ({ value: m.value, label: m.value, icon: m.icon, color: m.color })), []);
  const defaultShuttleNameOption = shuttle ? shuttleNameOptions.find((o) => o.value === shuttle.name) : null;

  const dispatch = useDispatch();
  const bookingId = booking.id;
  const showExchangeRate = booking.currency === 'USD';
  const title = shuttle ? 'Edit' : 'Create';

  useEffect(() => { if (!shuttle) reset(); }, [shuttle, reset]);
  useEffect(() => { if (showExchangeRate) dispatch(getExchangeRate()); }, [showExchangeRate, dispatch]);

  useEffect(() => {
    setValue('departure_date', new Date(booking.date_of_travel));
    setValue('return_date', new Date(booking.date_of_return));
    setValue('currency', booking.currency);
    if (transit) {
      setValue('departure_date', new Date(transit.from_date));
      setValue('return_date', new Date(transit.to_date));
    }
    if (showExchangeRate) setValue('exchange_rate', usdExchangeRateData);
    if (shuttle) {
      Object.entries(shuttle).forEach(([key, value]) => {
        if ((key === 'departure_date' || key === 'return_date')) {
          if (value) setValue(key, new Date(value));
        } else setValue(key, value);
      });
    }
    // seed only when booking/shuttle change; re-running when the exchange rate/transit update would overwrite the user's edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, booking, shuttle]);

  const closeModal = () => setshowModal(false);

  const onSubmit = (formData) => {
    let data = { ...formData, files };
    if (transit) data = { ...data, transit_id: transit.id };
    try {
      if (shuttle) dispatch(updateBookingShuttle({ bookingId, shuttleId: shuttle.id, data }));
      else dispatch(createBookingShuttle({ bookingId, data }));
      closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={<><FaBusinessTime className="tw-text-blue-600" /> {title} Shuttle booking</>}
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="shuttle-form" variant="primary">Save</Button>
        </>
      )}
    >
      <Alert type="warning" className="tw-mb-3">Book shuttle for your trip, including company vehicles, car rentals, flights, trains, taxis, and more.</Alert>
      {transit && <h5 className="tw-mb-3 tw-text-sm">Booking for: <span className="tw-text-blue-700">{transit.name}</span></h5>}

      <form id="shuttle-form" onSubmit={handleSubmit(onSubmit)}>
        <FormRow label="Name" required error={errors.name?.message}>
          <Controller
            control={control}
            name="name"
            rules={{ required: { value: true, message: 'Name is required' } }}
            render={() => (
              <Select
                closeMenuOnSelect
                options={shuttleNameOptions}
                defaultValue={defaultShuttleNameOption}
                placeholder="Select..."
                classNamePrefix="react-select" {...menuPortalProps}
                formatOptionLabel={formatShuttleOption}
                onChange={(opt) => { setValue('name', opt.value, { shouldValidate: true }); setValue('primary', opt.value === 'Plane'); }}
              />
            )}
          />
        </FormRow>

        <FormRow label="Description" required error={errors.description?.message}>
          <textarea rows={5} placeholder="Description" className={inputClass} {...register('description', { required: 'Description must be required.' })} />
        </FormRow>

        <FormRow label="Departure date" required error={errors.departure_date?.message}>
          <Controller control={control} name="departure_date" rules={{ required: 'Departure date must be required.' }}
            render={({ field }) => (
              <DatePicker selected={field.value} onChange={(d) => field.onChange(d)} className={inputClass}
                placeholderText="DD-MM-YYYY h:mm aa" timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect fixedHeight />
            )} />
        </FormRow>

        <FormRow label="Return date" required error={errors.return_date?.message}>
          <Controller control={control} name="return_date"
            rules={{ required: 'Return date must be required.', validate: { afterFromDate: (v) => isAfter(v, getValues('departure_date')) || 'Check out must be after check in time.' } }}
            render={({ field }) => (
              <DatePicker selected={field.value} onChange={(d) => field.onChange(d)} className={inputClass}
                placeholderText="DD-MM-YYYY h:mm aa" timeIntervals={5} dateFormat="dd-MM-yyyy h:mm aa" showTimeSelect fixedHeight />
            )} />
        </FormRow>

        <FormRow label="Primary">
          <Controller control={control} name="primary" defaultValue={false}
            render={({ field }) => (
              <label className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={!!field.value} onChange={(e) => setValue('primary', e.target.checked)} />
                <span className="tw-text-sm tw-text-slate-600">Set primary</span>
              </label>
            )} />
          <div className={hint}><FaInfoCircle /> When Primary is enabled, business travel expenses will be calculated based on the actual travel time of transportation.</div>
        </FormRow>

        <hr className="tw-border-slate-200" />

        <FormRow label="Amount estimate currency" required error={errors.amount_estimate_currency?.message}>
          <input placeholder="Amount estimate currency" className={inputClass} {...register('amount_estimate_currency', { required: 'Amount estimate currency must be required.' })} />
          <div className={hint}><FaInfoCircle /> Shuttle expense estimate.</div>
        </FormRow>

        <FormRow label="Amount actual">
          <input placeholder="Expense currency" className={inputClass} {...register('amount_currency')} />
          <div className={hint}><FaInfoCircle /> Actual shuttle expense.</div>
        </FormRow>

        <FormRow label="Currency" error={errors.currency?.message}>
          <CurrencySelect value={watch('currency')} onChange={(v) => setValue('currency', v, { shouldValidate: true })} />
          <div className={hint}><FaInfoCircle /> Defaults to the trip&apos;s currency; change if this item uses another.</div>
        </FormRow>

        {showExchangeRate && (
          <FormRow label="Exchange rate" error={errors.exchange_rate?.message}>
            <input type="text" placeholder="Exchange rate" className={inputClass} {...register('exchange_rate', { required: 'Exchange rate must be required' })} />
            <Alert type="info" className="tw-mt-2">USD exchange rate to VND from <a href="https://www.vietcombank.com.vn/KHCN/Cong-cu-tien-ich/Ty-gia?devicechannel=default" target="_blank" rel="noreferrer" className="tw-underline">Vietcombank</a>.</Alert>
          </FormRow>
        )}

        <hr className="tw-border-slate-200" />

        <FormRow label="Note">
          <Controller control={control} name="notes"
            render={({ field }) => <TinymceEditor height="30vh" handleChange={field.onChange} value={field.value} />} />
        </FormRow>

        <FormRow label="Attachments">
          <BookingUploadAttachment files={files} setFiles={setFiles} />
        </FormRow>
      </form>
    </Drawer>
  );
};

ShuttleForm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  booking: PropTypes.object.isRequired,
  shuttle: PropTypes.object,
  transit: PropTypes.object,
};

export default ShuttleForm;
